import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Search, User, Star } from "lucide-react";
import api from "@/utils/api";

export default function InviteCoInterpreterModal({ request, currentUserId, onClose, onInvited }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/interpreters"),
      api.get(`/interpreters/${currentUserId}`).catch(() => ({ data: {} })),
    ])
      .then(([list, me]) => {
        setResults(list.data.filter((x) => x.user_id !== currentUserId));
        setFavorites(me.data?.favorite_co_interpreters || []);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [currentUserId]);

  const toggleFav = async (e, otherId) => {
    e.stopPropagation();
    try {
      const r = await api.post(`/interpreters/${currentUserId}/favorite-cos`, {
        favorite_user_id: otherId,
      });
      setFavorites(r.data.favorites || []);
    } catch (err) {
      // silent — keep UI responsive
    }
  };

  const filtered = results.filter((p) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      (p.name || "").toLowerCase().includes(q) ||
      (p.service_city || "").toLowerCase().includes(q) ||
      (p.specializations || []).some((s) => s.toLowerCase().includes(q))
    );
  });
  const favSet = new Set(favorites);
  const favItems = filtered.filter((p) => favSet.has(p.user_id));
  const restItems = filtered.filter((p) => !favSet.has(p.user_id));

  const renderCandidate = (p) => (
    <div
      key={p.user_id}
      className="w-full flex items-center gap-3 p-3 border border-gray-200 rounded-xl hover:border-[#14B8A6] hover:bg-[#14B8A6]/5 transition-all"
    >
      <button
        onClick={() => handleInvite(p.user_id)}
        disabled={sending}
        data-testid={`invite-candidate-${p.user_id}`}
        className="flex-1 flex items-center gap-3 text-left disabled:opacity-50"
      >
        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center shrink-0">
          <User size={18} className="text-gray-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm text-gray-900 truncate">{p.name || "Unbenannt"}</p>
          <p className="text-xs text-gray-500 truncate">
            {p.service_city || "—"}
            {p.hourly_rate > 0 && ` · ${p.hourly_rate} €/h`}
          </p>
          {p.specializations?.length > 0 && (
            <p className="text-xs text-gray-400 truncate mt-0.5">{p.specializations.slice(0, 3).join(", ")}</p>
          )}
        </div>
      </button>
      <button
        onClick={(e) => toggleFav(e, p.user_id)}
        data-testid={`fav-toggle-${p.user_id}`}
        title={favSet.has(p.user_id) ? "Aus Favoriten entfernen" : "Zu Favoriten hinzufügen"}
        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-full hover:bg-amber-50 transition-colors"
      >
        <Star
          size={18}
          className={favSet.has(p.user_id) ? "text-amber-400" : "text-gray-300"}
          fill={favSet.has(p.user_id) ? "currentColor" : "none"}
        />
      </button>
    </div>
  );

  const handleInvite = async (coId) => {
    setSending(true);
    setError("");
    try {
      await api.post(`/requests/${request.id}/co-interpreter`, { co_interpreter_id: coId });
      onInvited && onInvited();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || "Einladung fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
        onClick={onClose}
        data-testid="invite-co-modal"
      >
        <motion.div
          initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] flex flex-col"
        >
          <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-3">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Co-Dolmetscher:in einladen</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Für: {request.client_name} · {request.event_date} {request.event_time && `· ${request.event_time}–${request.event_time_end || ""}`}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1" aria-label="Schließen">
              <X size={20} />
            </button>
          </div>

          <div className="p-4 border-b border-gray-100">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, Stadt oder Fachgebiet…"
                data-testid="co-search-input"
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#14B8A6]"
                autoFocus
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {loading && <p className="text-sm text-gray-500 text-center py-8">Lädt…</p>}
            {!loading && filtered.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-8">Keine Dolmetscher:innen gefunden</p>
            )}
            {!loading && favItems.length > 0 && (
              <>
                <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-amber-700 mt-1 mb-1">
                  <Star size={12} className="fill-amber-400 text-amber-400" />
                  Deine Favoriten
                </div>
                {favItems.map(renderCandidate)}
              </>
            )}
            {!loading && favItems.length > 0 && restItems.length > 0 && (
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mt-3 mb-1">
                Alle Dolmetscher:innen
              </div>
            )}
            {!loading && restItems.map(renderCandidate)}
          </div>

          {error && (
            <div className="m-4 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg" data-testid="invite-co-error">
              {error}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
